import { z } from 'zod';

import {
    buildBindingPresentation,
    type DynamicWidgetBindingPresentation,
    type DynamicWidgetIssue,
    type DynamicWidgetModelBuilder,
    type ShiftListWidgetDayGroup,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    decodeFutureOccurrenceId,
    decodeLegacyShiftRow,
    formatLegacyShiftTimeLabel,
    formatShiftDayLabel,
    groupShiftRowIndexesByDay,
    nullableShiftNumberSchema,
    nullableShiftStringSchema,
    shiftStringArraySchema,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_model';
import { splitShiftDatetime } from '@apps/web/components/phoebe_chat_view/tool_calls/shift_datetime';

export const shiftListValidationFields = {
    critical: ['shifts', 'shift_id', 'patient_name'],
    optional: [
        'shift_day',
        'start_time',
        'end_time',
        'city',
        'status',
        'frequency',
        'caregiver_id',
        'assigned_caregiver_id',
        'caregiver_name',
        'assigned_caregiver_name',
        'series_coverage',
        'role_required',
        'skills_required',
        'future_occurrences',
        'message',
        'hint',
    ],
} as const;

function bindingPresentation(
    section: string,
    label: string,
    rowKey: string | null = null,
    role: DynamicWidgetBindingPresentation['role'] = 'field',
): DynamicWidgetBindingPresentation {
    return buildBindingPresentation({ section, rowKey, label, role });
}

function rowBindingPresentation(
    rowIndex: number,
    label: string,
    role: DynamicWidgetBindingPresentation['role'] = 'field',
): DynamicWidgetBindingPresentation {
    return bindingPresentation('rows', label, `rows[${rowIndex}]`, role);
}

function dayGroupBindingPresentation(
    groupIndex: number,
    label: string,
): DynamicWidgetBindingPresentation {
    return bindingPresentation('dayGroups', label, `dayGroups[${groupIndex}]`);
}

export function cardBindingPresentation(
    label: string,
    role: DynamicWidgetBindingPresentation['role'] = 'field',
): DynamicWidgetBindingPresentation {
    return bindingPresentation('card', label, null, role);
}

type OptionalSourceFieldOptions<T> = {
    readonly builder: DynamicWidgetModelBuilder;
    readonly issues: Array<DynamicWidgetIssue>;
    readonly sourceId: string;
    readonly id: string;
    readonly path: string;
    readonly rawValue: unknown;
    readonly schema: z.ZodType<T>;
    readonly fallback: T;
    readonly renderedValue: (value: T) => string | null;
    readonly presentation: ReturnType<typeof buildBindingPresentation>;
    readonly rowIndex?: number;
    readonly issueWhenMissing?: boolean;
};

function addSourceIssue({
    issues,
    sourceId,
    bindingId,
    code,
    field,
    rowIndex,
}: {
    readonly issues: Array<DynamicWidgetIssue>;
    readonly sourceId: string;
    readonly bindingId: string;
    readonly code: 'source_invalid' | 'source_missing';
    readonly field: string;
    readonly rowIndex?: number;
}) {
    issues.push({
        id: `${bindingId}.issue`,
        code,
        level: 'warning',
        scope: 'field',
        bindingIds: [bindingId],
        sourceIds: [sourceId],
        retryable: false,
        safeDetails: {
            field,
            rowIndex: rowIndex ?? null,
        },
    });
}

export function addOptionalSourceField<T>(
    options: OptionalSourceFieldOptions<T>,
): T {
    const parsed =
        options.rawValue === undefined
            ? null
            : options.schema.safeParse(options.rawValue);
    const value =
        parsed === null || !parsed.success ? options.fallback : parsed.data;
    options.builder.addSourceField({
        id: options.id,
        sourceId: options.sourceId,
        path: options.path,
        rawValue: options.rawValue,
        value,
        renderedValue: options.renderedValue(value),
        presentation: options.presentation,
    });
    if (
        (parsed !== null && !parsed.success) ||
        (parsed === null && options.issueWhenMissing)
    ) {
        addSourceIssue({
            issues: options.issues,
            sourceId: options.sourceId,
            bindingId: options.id,
            code: parsed === null ? 'source_missing' : 'source_invalid',
            field: options.path,
            rowIndex: options.rowIndex,
        });
    }
    return value;
}

function addOptionalString({
    row,
    key,
    rowIndex,
    sourceRowIndex,
    builder,
    issues,
    sourceId,
    issueWhenMissing,
}: {
    readonly row: Record<string, unknown>;
    readonly key: string;
    readonly rowIndex: number;
    readonly sourceRowIndex?: number;
    readonly builder: DynamicWidgetModelBuilder;
    readonly issues: Array<DynamicWidgetIssue>;
    readonly sourceId: string;
    readonly issueWhenMissing?: boolean;
}): string | null {
    return addOptionalSourceField({
        builder,
        issues,
        sourceId,
        id: `rows.${rowIndex}.${key}`,
        path: `/shifts/${sourceRowIndex ?? rowIndex}/${key}`,
        rawValue: row[key],
        schema: nullableShiftStringSchema,
        fallback: null,
        renderedValue: (value) => value,
        presentation: rowBindingPresentation(rowIndex, key),
        rowIndex: sourceRowIndex ?? rowIndex,
        issueWhenMissing,
    });
}

export function buildShiftRow({
    rawRow,
    rowIndex,
    sourceRowIndex,
    builder,
    issues,
    sourceId,
}: {
    readonly rawRow: unknown;
    readonly rowIndex: number;
    readonly sourceRowIndex: number;
    readonly builder: DynamicWidgetModelBuilder;
    readonly issues: Array<DynamicWidgetIssue>;
    readonly sourceId: string;
}): ShiftListWidgetRow | null {
    const decodedRow = decodeLegacyShiftRow(rawRow);
    if (!decodedRow.success) {
        const { field, rawValue } = decodedRow;
        const bindingId = `droppedRows.${sourceRowIndex}.${field}`;
        builder.addSourceField({
            id: bindingId,
            sourceId,
            path:
                field === 'row'
                    ? `/shifts/${sourceRowIndex}`
                    : `/shifts/${sourceRowIndex}/${field}`,
            rawValue,
            value: null,
            renderedValue: null,
            presentation: bindingPresentation(
                'droppedRows',
                field,
                `droppedRows[${sourceRowIndex}]`,
            ),
        });
        issues.push({
            id: `${bindingId}.issue`,
            code: rawValue === undefined ? 'source_missing' : 'source_invalid',
            level: 'warning',
            scope: 'section',
            bindingIds: [bindingId],
            sourceIds: [sourceId],
            retryable: false,
            safeDetails: {
                field,
                rowIndex: sourceRowIndex,
            },
        });
        return null;
    }

    const row = decodedRow.row;
    const rowPrefix = `/shifts/${sourceRowIndex}`;
    const shiftId = builder.addSourceField({
        id: `rows.${rowIndex}.shiftId`,
        sourceId,
        path: `${rowPrefix}/shift_id`,
        rawValue: row['shift_id'],
        value: row['shift_id'],
        renderedValue: row['shift_id'],
        presentation: rowBindingPresentation(rowIndex, 'shiftId'),
    });
    const patientName = builder.addSourceField({
        id: `rows.${rowIndex}.patientName`,
        sourceId,
        path: `${rowPrefix}/patient_name`,
        rawValue: row['patient_name'],
        value: row['patient_name'],
        renderedValue: row['patient_name'],
        presentation: rowBindingPresentation(rowIndex, 'patientName'),
    });
    const startTime = addOptionalString({
        row,
        key: 'start_time',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const endTime = addOptionalString({
        row,
        key: 'end_time',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const explicitDay = addOptionalString({
        row,
        key: 'shift_day',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const startDay = splitShiftDatetime(startTime).day;
    const day = builder.addCalculatedField({
        id: `rows.${rowIndex}.day`,
        sourceIds: [sourceId],
        paths: [`${rowPrefix}/shift_day`, `${rowPrefix}/start_time`],
        transform: 'preferShiftDayThenStartDate',
        rawValue: {
            shiftDay: explicitDay,
            startTime,
        },
        value: explicitDay ?? startDay,
        renderedValue: explicitDay ?? startDay,
        presentation: rowBindingPresentation(rowIndex, 'day'),
    });
    const calculatedTimeLabel = formatLegacyShiftTimeLabel(startTime, endTime);
    const timeLabel = builder.addCalculatedField({
        id: `rows.${rowIndex}.timeLabel`,
        sourceIds: [sourceId],
        paths: [`${rowPrefix}/start_time`, `${rowPrefix}/end_time`],
        transform: 'formatShiftTimeRange',
        rawValue: {
            startTime,
            endTime,
        },
        value: calculatedTimeLabel,
        renderedValue: calculatedTimeLabel,
        presentation: rowBindingPresentation(rowIndex, 'timeLabel'),
    });

    const primaryCaregiverId = addOptionalString({
        row,
        key: 'caregiver_id',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const assignedCaregiverId = addOptionalString({
        row,
        key: 'assigned_caregiver_id',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const caregiverId = builder.addCalculatedField({
        id: `rows.${rowIndex}.caregiverId`,
        sourceIds: [sourceId],
        paths: [
            `${rowPrefix}/caregiver_id`,
            `${rowPrefix}/assigned_caregiver_id`,
        ],
        transform: 'preferCaregiverId',
        rawValue: {
            assignedCaregiverId,
            primaryCaregiverId,
        },
        value: primaryCaregiverId ?? assignedCaregiverId,
        renderedValue: primaryCaregiverId ?? assignedCaregiverId,
        presentation: rowBindingPresentation(rowIndex, 'caregiverId'),
    });
    const primaryCaregiverName = addOptionalString({
        row,
        key: 'caregiver_name',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const assignedCaregiverName = addOptionalString({
        row,
        key: 'assigned_caregiver_name',
        rowIndex,
        sourceRowIndex,
        builder,
        issues,
        sourceId,
    });
    const caregiverName = builder.addCalculatedField({
        id: `rows.${rowIndex}.caregiverName`,
        sourceIds: [sourceId],
        paths: [
            `${rowPrefix}/caregiver_name`,
            `${rowPrefix}/assigned_caregiver_name`,
        ],
        transform: 'preferCaregiverName',
        rawValue: {
            assignedCaregiverName,
            primaryCaregiverName,
        },
        value: primaryCaregiverName ?? assignedCaregiverName,
        renderedValue: primaryCaregiverName ?? assignedCaregiverName,
        presentation: rowBindingPresentation(rowIndex, 'caregiverName'),
    });

    const rawCoverage =
        typeof row['series_coverage'] === 'object' &&
        row['series_coverage'] !== null &&
        !Array.isArray(row['series_coverage'])
            ? (row['series_coverage'] as Record<string, unknown>)
            : null;
    const seriesOpen = addOptionalSourceField({
        builder,
        issues,
        sourceId,
        id: `rows.${rowIndex}.seriesOpen`,
        path: `${rowPrefix}/series_coverage/open_occurrences`,
        rawValue:
            rawCoverage?.['open_occurrences'] ??
            (row['series_coverage'] === undefined
                ? undefined
                : row['series_coverage']),
        schema: nullableShiftNumberSchema,
        fallback: null,
        renderedValue: (value) => (value === null ? null : String(value)),
        presentation: rowBindingPresentation(rowIndex, 'seriesOpen'),
        rowIndex: sourceRowIndex,
    });
    const seriesCovered = addOptionalSourceField({
        builder,
        issues,
        sourceId,
        id: `rows.${rowIndex}.seriesCovered`,
        path: `${rowPrefix}/series_coverage/covered_occurrences`,
        rawValue:
            rawCoverage?.['covered_occurrences'] ??
            (row['series_coverage'] === undefined
                ? undefined
                : row['series_coverage']),
        schema: nullableShiftNumberSchema,
        fallback: null,
        renderedValue: (value) => (value === null ? null : String(value)),
        presentation: rowBindingPresentation(rowIndex, 'seriesCovered'),
        rowIndex: sourceRowIndex,
    });
    const skillsRequired = addOptionalSourceField({
        builder,
        issues,
        sourceId,
        id: `rows.${rowIndex}.skillsRequired`,
        path: `${rowPrefix}/skills_required`,
        rawValue: row['skills_required'],
        schema: shiftStringArraySchema,
        fallback: [],
        renderedValue: (value) =>
            value.length === 0 ? null : value.join(' · '),
        presentation: rowBindingPresentation(rowIndex, 'skillsRequired'),
        rowIndex: sourceRowIndex,
    });

    const rawFutureOccurrences = row['future_occurrences'];
    const futureOccurrenceIds: Array<string> = [];
    const futureOccurrencePaths: Array<string> = [];
    if (rawFutureOccurrences !== undefined) {
        const parsedFutureOccurrences = z
            .array(z.unknown())
            .safeParse(rawFutureOccurrences);
        if (!parsedFutureOccurrences.success) {
            addSourceIssue({
                issues,
                sourceId,
                bindingId: `rows.${rowIndex}.futureOccurrenceIds`,
                code: 'source_invalid',
                field: `${rowPrefix}/future_occurrences`,
                rowIndex: sourceRowIndex,
            });
        } else {
            parsedFutureOccurrences.data.forEach(
                (occurrence, occurrenceIndex) => {
                    const occurrencePath =
                        `${rowPrefix}/future_occurrences/` +
                        `${occurrenceIndex}/shift_id`;
                    const occurrenceId = decodeFutureOccurrenceId(occurrence);
                    if (occurrenceId !== null) {
                        futureOccurrenceIds.push(occurrenceId);
                        futureOccurrencePaths.push(occurrencePath);
                        return;
                    }
                    addSourceIssue({
                        issues,
                        sourceId,
                        bindingId: `rows.${rowIndex}.futureOccurrenceIds`,
                        code: 'source_invalid',
                        field: occurrencePath,
                        rowIndex: sourceRowIndex,
                    });
                },
            );
        }
    }
    const boundFutureOccurrenceIds = builder.addCalculatedField({
        id: `rows.${rowIndex}.futureOccurrenceIds`,
        sourceIds: [sourceId],
        paths:
            futureOccurrencePaths.length > 0
                ? futureOccurrencePaths
                : [`${rowPrefix}/future_occurrences`],
        transform: 'extractFutureOccurrenceIds',
        rawValue: rawFutureOccurrences,
        value: futureOccurrenceIds,
        renderedValue:
            futureOccurrenceIds.length === 0
                ? null
                : JSON.stringify(futureOccurrenceIds),
        presentation: rowBindingPresentation(
            rowIndex,
            'futureOccurrenceIds',
            'control',
        ),
    });
    const calculatedOccurrenceIds = Array.from(
        new Set([shiftId, ...boundFutureOccurrenceIds]),
    );
    const occurrenceIds = builder.addCalculatedField({
        id: `rows.${rowIndex}.occurrenceIds`,
        sourceIds: [sourceId],
        paths: [`${rowPrefix}/shift_id`, ...futureOccurrencePaths],
        transform: 'deduplicateOccurrenceIds',
        rawValue: [shiftId, ...boundFutureOccurrenceIds],
        value: calculatedOccurrenceIds,
        renderedValue: JSON.stringify(calculatedOccurrenceIds),
        presentation: rowBindingPresentation(
            rowIndex,
            'occurrenceIds',
            'control',
        ),
    });

    return {
        shiftId,
        patientName,
        day,
        timeLabel,
        city: addOptionalString({
            row,
            key: 'city',
            rowIndex,
            sourceRowIndex,
            builder,
            issues,
            sourceId,
        }),
        status: addOptionalString({
            row,
            key: 'status',
            rowIndex,
            sourceRowIndex,
            builder,
            issues,
            sourceId,
            issueWhenMissing: true,
        }),
        frequency: addOptionalString({
            row,
            key: 'frequency',
            rowIndex,
            sourceRowIndex,
            builder,
            issues,
            sourceId,
        }),
        caregiverId,
        caregiverName,
        seriesOpen,
        seriesCovered,
        roleRequired: addOptionalString({
            row,
            key: 'role_required',
            rowIndex,
            sourceRowIndex,
            builder,
            issues,
            sourceId,
        }),
        skillsRequired,
        futureOccurrenceIds: boundFutureOccurrenceIds,
        occurrenceIds,
    };
}

export function buildDayGroups(
    rows: ReadonlyArray<ShiftListWidgetRow>,
    sourceRowIndexes: ReadonlyArray<number>,
    builder: DynamicWidgetModelBuilder,
    sourceId: string,
): Array<ShiftListWidgetDayGroup> {
    const groups = groupShiftRowIndexesByDay(rows);

    return groups.map((group, groupIndex) => {
        const dayPaths = group.rowIndexes.flatMap((rowIndex) => [
            `/shifts/${sourceRowIndexes[rowIndex]}/shift_day`,
            `/shifts/${sourceRowIndexes[rowIndex]}/start_time`,
        ]);
        const day = builder.addCalculatedField({
            id: `dayGroups.${groupIndex}.day`,
            sourceIds: [sourceId],
            paths: dayPaths,
            transform: 'groupAdjacentShiftDays',
            rawValue: group.rowIndexes.map(
                (rowIndex) => rows[rowIndex]?.day ?? null,
            ),
            value: group.day,
            renderedValue: group.day,
            presentation: dayGroupBindingPresentation(groupIndex, 'day'),
        });
        const calculatedDayLabel = formatShiftDayLabel(day);
        const dayLabel = builder.addCalculatedField({
            id: `dayGroups.${groupIndex}.dayLabel`,
            sourceIds: [sourceId],
            paths: dayPaths,
            transform: 'formatDayLabel',
            rawValue: day,
            value: calculatedDayLabel,
            renderedValue: calculatedDayLabel,
            presentation: dayGroupBindingPresentation(groupIndex, 'dayLabel'),
        });
        const rowIndexes = builder.addCalculatedField({
            id: `dayGroups.${groupIndex}.rowIndexes`,
            sourceIds: [sourceId],
            paths: dayPaths,
            transform: 'groupAdjacentShiftRowIndexes',
            rawValue: group.rowIndexes,
            value: group.rowIndexes,
            renderedValue: JSON.stringify(group.rowIndexes),
            presentation: dayGroupBindingPresentation(groupIndex, 'rowIndexes'),
        });
        return {
            day,
            dayLabel,
            rowIndexes,
        };
    });
}
